/**
 * Tabla de ingresos del Panel People, compartida por las tabs Proceso /
 * Completados / Con errores (reestructura pedida por KP 14/09). Filtro por
 * clapbase + búsqueda, pipeline por etapas, columna "Compu" (asignar desde el
 * panel) y el detalle por clapper en un diálogo deep-linkeable (?alta=).
 */

import { currentLocale } from '@/lib/locale';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CalendarClock, Search } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { flowProgress } from '@/components/users/onboarding-flow/flow-model';
import { FlowStagePipeline, FlowLegend } from '@/components/users/onboarding-flow/FlowStagePipeline';
import { FlowDetailDialog } from '@/components/users/onboarding-flow/FlowDetailDialog';
import { EquipmentCell } from './EquipmentCell';
import { useCanAssignEquipment } from './useCanAssignEquipment';
import type { RowWithSteps } from './usePeopleFlowRows';

export function AltasTable({
  rows,
  allRows,
  emptyText,
  lastRunAt,
  toolbarExtra,
}: {
  /** Lo que muestra esta tab. */
  rows: RowWithSteps[];
  /** Todas las filas: el diálogo (?alta=) tiene que abrir aunque el alta viva en otra tab. */
  allRows: RowWithSteps[];
  emptyText: string;
  lastRunAt?: string | null;
  toolbarExtra?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clapbase, setClapbase] = useState<string>('all');
  const [search, setSearch] = useState('');
  const canAssign = useCanAssignEquipment();

  // Una sola query para toda la tabla; la celda solo mira su alta.
  const equipment = trpc.peopleFlow.getEquipmentContext.useQuery(undefined, { staleTime: 60_000 });

  const clapbases = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.row.alta.comunidad).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, 'es')
      ),
    [rows]
  );

  const filtered = useMemo(() => {
    let out = rows;
    if (clapbase !== 'all') out = out.filter((r) => r.row.alta.comunidad === clapbase);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.row.alta.nombre, r.row.alta.correoWinclap, r.row.alta.correoPersonal, r.row.alta.comunidad]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    return out;
  }, [rows, clapbase, search]);
  const { pageItems: pageRows, footer: paginationFooter } = usePagination(filtered, 25);

  const openAltaId = searchParams.get('alta');
  const openRow = openAltaId ? allRows.find((r) => r.row.alta.idAlta === openAltaId) ?? null : null;
  const setOpenAlta = (idAlta: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (idAlta) params.set('alta', idAlta);
    else params.delete('alta');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {toolbarExtra}
        <Select value={clapbase} onValueChange={setClapbase}>
          <SelectTrigger className="h-8 w-[190px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('users.people_panel.filters.clapbase_all')}</SelectItem>
            {clapbases.map((cb) => (
              <SelectItem key={cb} value={cb}>{cb}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative ml-auto w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('users.people_panel.search_placeholder')}
            className="h-9 pl-8"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <FlowLegend />
        {lastRunAt && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            {t('users.people_panel.last_run')}: {new Date(lastRunAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.people_panel.table.start_date')}</TableHead>
              <TableHead>{t('users.people_panel.table.name')}</TableHead>
              <TableHead>{t('users.people_panel.table.clapbase')}</TableHead>
              <TableHead>{t('users.people_panel.table.pipeline')}</TableHead>
              <TableHead>{t('users.people_panel.table.equipment')}</TableHead>
              <TableHead className="text-right">{t('users.people_panel.table.progress')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
            {pageRows.map(({ row, allSteps }) => {
              const progress = flowProgress(allSteps);
              return (
                <TableRow
                  key={row.alta.idAlta}
                  className="cursor-pointer"
                  onClick={() => setOpenAlta(row.alta.idAlta)}
                >
                  <TableCell className="whitespace-nowrap">{row.alta.fechaIngreso}</TableCell>
                  <TableCell>
                    <div className="font-medium">{row.alta.nombre}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.alta.correoWinclap || row.alta.correoPersonal}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {row.alta.comunidad || '—'}
                    </span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <FlowStagePipeline
                      steps={allSteps}
                      validationMissing={row.validation.missingLabels}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <EquipmentCell
                      alta={row.alta}
                      equipment={equipment.data?.[row.alta.idAlta]}
                      canAssign={canAssign}
                    />
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                    {`${progress.done}/${progress.total}`}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      {paginationFooter}

      {openRow && (
        <FlowDetailDialog
          row={openRow.row}
          legacySteps={openRow.legacySteps}
          onClose={() => setOpenAlta(null)}
        />
      )}
    </div>
  );
}
